import { useEffect, useState } from "react";
  import { createPortal } from "react-dom";
  import { useTranslation } from "react-i18next";
  import { useNavigate } from "react-router-dom";
  import backarrow from "../../../assets/images/back-arrow-left.svg";
  import closeicon from "../../../assets/images/close.svg";
  import useAmanFlow from "../../../hooks/useAmanFlow";
  import useLanguageTranslate from "../../../hooks/useLanguageTranslate";

  const AmanJourneyLayout = ({
    children,
    is_hide_back_btn,
    show_close_btn,
    onClose,
    customBackHandler,
    isFirstPage = false,
  }) => {
    const navigate = useNavigate();
    const { handlePrevSubstep } = useAmanFlow();
    const translate = useLanguageTranslate();
    const { i18n } = useTranslation();
    const isRTL = i18n.resolvedLanguage === "ar";

    // Portal the mobile back button into a stable in-tree node provided by
    // the host page (#aman-back-portal), so an animating ancestor cannot
    // become the containing block for this fixed button. Falls back to
    // inline render when no target is present.
    const [backPortalTarget, setBackPortalTarget] = useState(null);
    useEffect(() => {
      if (typeof document === "undefined") return;
      setBackPortalTarget(document.getElementById("aman-back-portal"));
    }, []);

    const handleNavigationClick = () => {
      if (customBackHandler) {
        customBackHandler();
      } else if (isFirstPage || show_close_btn) {
        if (onClose) {
          onClose();
        } else {
          navigate("/new-policy");
        }
      } else {
        handlePrevSubstep();
      }
    };

    return (
      <div className="aman-journey-layout">
        {/* Mobile Navigation - Fixed close/back button (visible <=767px).
            Portaled to #aman-back-portal in NewAmanPolicy so an animating
            ancestor (e.g. framer-motion page-transition wrapper that sets
            `transform`) can't become the containing block for this
            position:fixed button. */}
        {(!is_hide_back_btn || isFirstPage || show_close_btn) && (() => {
          const _mobileBack = (
            <div className="block md:hidden">
              <button
                type="button"
                className={`flex items-center justify-center w-10 h-10 fixed top-3 rounded-full bg-[#F6F8F9] hover:bg-gray-200 transition-colors z-[100] ${
                  isRTL ? "right-4" : "left-4"
                }`}
                aria-label={
                  isFirstPage || show_close_btn
                    ? translate("Close", "إغلاق")
                    : translate("Back", "رجوع")
                }
                onClick={handleNavigationClick}
              >
                {isFirstPage || show_close_btn ? (
                  <img src={closeicon} alt="Close" className="w-5 h-5" />
                ) : (
                  <img
                    src={backarrow}
                    alt="Back"
                    className={`w-5 h-5 ${isRTL ? "rotate-180" : ""}`}
                  />
                )}
              </button>
            </div>
          );
          return backPortalTarget
            ? createPortal(_mobileBack, backPortalTarget)
            : _mobileBack;
        })()}

        {/* Desktop back button - hidden on first/close page */}
        {!is_hide_back_btn && !isFirstPage && !show_close_btn && (
          <button
            className="hidden md:flex backButton bg-navy-blue rounded mt-5 absolute left-[26px] rtl:right-[26px] rtl:left-auto top-0 text-black text-sm not-italic font-semibold leading-6 tracking-[-0.084px]"
            onClick={customBackHandler || handlePrevSubstep}
          >
            <img
              src={backarrow}
              alt="Back arrow"
              className="rtl:ml-[5px] rtl:rotate-[181deg]"
            />
            <span className="back-text inline">
              {translate("Back", "رجوع")}
            </span>
          </button>
        )}

        <div className="flex flex-col items-center justify-start max767:pt-0">
          {children}
        </div>
      </div>
    );
  };

  export default AmanJourneyLayout;
  